import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { DriverApi } from "../driver-api.ts";
import {
  DESIGNED_METRICS,
  earningsChip,
  earningsHeadline,
  earningsRows,
  metricRow,
  qualityChip,
  qualitySubline,
  ratingLabel,
  type EarningsView,
} from "../earnings.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Earnings` and `Quality score`, from one read of EXP-003's `earnings`.
 *
 * Two screens over one operation because the design draws them as two faces of the same fact, and
 * because EXP-003 composes RAT-03, DRV-07 and FIN-07 into one answer for exactly that reason.
 *
 * ## Earnings is almost entirely an absence, and that is the point of building it
 *
 * **Nothing on this platform publishes what a courier has earned.** FIN-06's commission publishes
 * two operations and both are `/internal/`; FIN-07 publishes one payout read by identifier and no
 * listing; and no per-driver earnings read exists anywhere. So the headline the design writes as
 * `TZS 48,200` has no source, and the four rows under it have none either.
 *
 * The temptation is a zero, and it is the one thing this screen must not draw. A zero is a number a
 * courier acts on — they conclude they worked a week for nothing — and there is no accounting behind
 * it. EXP-003 answers `earnings: null` with an `earningsAbsence` naming which of three facts it is,
 * and this screen draws that: the design's own dashed `unknown` pill, the design's own geometry, and
 * words that say which kind of nothing.
 *
 * ## Quality is mostly real, and loses only its targets
 *
 * RAT-03 answers the rating and DRV-07 answers one window per metric with a numerator and a
 * denominator, so `4.8 rating` and `86%` are both published. `target 80%` is not: nothing publishes
 * `aggregate.metricName`, `aggregate.target` or `aggregate.targetBasisPoints`. So each row carries
 * its percentage and the target is the one drawn element this screen loses.
 */

export interface EarningsProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  readonly driverId: string | undefined;
  /** Which of the two screens to draw. They share a read and nothing else. */
  readonly screen: "earnings" | "quality-score";
  /**
   * The window the figures are over, both ends required.
   *
   * DRV-07 requires both and EXP-003 refuses to default either, because defaulting one would be a
   * service deciding what "this week" means on somebody's earnings screen. The design says
   * `this week`, so the caller computes a week and the screen says which one.
   */
  readonly windowStart: string;
  readonly windowEnd: string;
  /**
   * FIN-07's identifier for a payout, where a deployment can supply one.
   *
   * FIN-07 publishes no listing, so there is no way to discover a courier's payouts and this
   * application has nowhere to get one. Without it EXP-003 answers `payout_not_requested`, which is
   * the one of the three absences a caller can fix — and saying which it is, is the whole reason
   * EXP-003 publishes three rather than one.
   */
  readonly payoutId?: string;
}

export function Earnings(props: EarningsProps): ReactNode {
  const [view, setView] = useState<EarningsView | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const { api, tenantId, driverId, windowStart, windowEnd, payoutId } = props;

  const load = useCallback(() => {
    if (driverId === undefined) return;
    setState({ kind: "loading" });
    api
      .earnings({
        tenantId,
        driverId,
        metrics: DESIGNED_METRICS,
        windowStart,
        windowEnd,
        ...(payoutId === undefined ? {} : { payoutId }),
      })
      .then((answer) => {
        setView(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, ["driver-app:read"])));
  }, [api, tenantId, driverId, windowStart, windowEnd, payoutId]);

  useEffect(load, [load]);

  if (driverId === undefined) {
    return (
      <StateScreen
        screen="earnings"
        chip={{ class: "unknown", label: "no courier id" }}
        title="We don't know which courier you are"
        sub="Your sign-in carries no courier identifier, so there is nothing to look up. Whoever administers your account sets that."
      />
    );
  }

  if (state.kind !== "ready" || view === undefined) {
    return (
      <StateScreen
        screen="earnings"
        state={state}
        onRetry={load}
        purpose={
          props.screen === "earnings" ? "Looking up your week." : "Looking up your standing."
        }
      />
    );
  }

  return props.screen === "quality-score" ? quality(view) : money(view);
}

/* ------------------------------------------------------------------------------- Earnings */

function money(view: EarningsView): ReactNode {
  const { headline, isAbsence } = earningsHeadline(view);
  return (
    <PhoneShell
      screen="earnings"
      /*
       * `box('payments','Paid to wallet','#12B76A')` — the design's one tinted disc.
       *
       * A green disc captioned "Paid to wallet" over an absence would be a courier told their money
       * arrived when nobody knows, which is the R-09 over-claim with money as its subject. So the
       * caption changes with the fact and the tint goes with it: the same geometry, the same weight,
       * a grey disc and words that are true.
       */
      art={
        <HeroDisc
          glyph="payments"
          caption={isAbsence ? "Not published" : "Paid to wallet"}
          {...(isAbsence ? { captionIsAbsence: true } : { tinted: true })}
        />
      }
      online={false}
      earningsLabel={null}
      chip={earningsChip(view)}
      title={headline}
      /*
       * `22 jobs · 31 h online · paid Friday` — three figures, none published.
       *
       * The job count would need a per-courier job listing, the hours would need
       * `availability.onlineSeconds` or `summary.onlineSeconds`, and the payout day would need a
       * payout listing. So the sub-line says what the screen is about and the rows below say which
       * figures are missing, rather than printing three plausible numbers.
       */
      sub="This week"
      rows={earningsRows(view)}
      actions={[
        {
          label: "See statement",
          glyph: "receipt_long",
          primary: false,
          unavailable:
            "Nothing publishes a courier statement: FIN-06's commission operations are /internal/ only and FIN-07 publishes no payout listing.",
        },
      ]}
      {...(isAbsence ? { flag: "figure not published · wording not in the design" } : {})}
    />
  );
}

/* ---------------------------------------------------------------------------- Quality score */

function quality(view: EarningsView): ReactNode {
  const rating = view.rating;
  return (
    <PhoneShell
      screen="earnings"
      art={
        rating === null ? (
          <HeroDisc glyph="payments" caption="No rating yet" captionIsAbsence />
        ) : (
          <HeroDisc glyph="payments" caption="Paid to wallet" tinted />
        )
      }
      online={false}
      earningsLabel={null}
      chip={qualityChip(view)}
      /*
       * `4.8 rating`, from RAT-03's `averageScoreBasisPoints`.
       *
       * That field is the mean score times ten thousand over facts whose `scoreUnits` are integers
       * `0..5` — read from RAT-03's own `quality.ts` rather than inferred from the field's name, and
       * getting it wrong would be wrong by a factor of a hundred. `ratingLabel` does the division.
       */
      title={
        rating === null ? "No rating yet" : `${ratingLabel(rating.averageScoreBasisPoints)} rating`
      }
      sub={qualitySubline(view)}
      /*
       * `Acceptance 86% · target 80%` — the percentage is published and the target is not.
       *
       * The three metric names are the design's rows turned into identifiers. DRV-07 types `metric`
       * as a free string in its path and publishes no set of them, so if it knows them by other
       * names every row comes back `no_window` — which is visible on the screen rather than silent.
       */
      rows={view.metrics.map(metricRow)}
      note={{
        kind: "info",
        text: "Only your last 100 jobs count, so one bad day fades quickly.",
      }}
      actions={[]}
      flag="targets not published"
    />
  );
}
